import * as THREE from "three";
import { Kit, instance, revolve, solGradient, solMark, type V2 } from "./kit";
import { glowSprite } from "./fx";

/** SOL: a thick bevelled coin, the Solana gradient on its rim and the mark embossed on both faces. */

const R = 0.58;
const SEG = 44;
const FIELD = 0.068;     // the recessed face
const RIM = 0.1;         // the raised lip

/** The logo's diagonal (bottom-left purple → top-right green), mirrored on the back so both faces read the same. */
function gradient(p: THREE.Vector3): THREE.Color {
  const side = THREE.MathUtils.clamp(p.z / 0.055, -1, 1);
  return solGradient(0.5 + (0.5 * p.x * side + 0.86 * p.y) / (2 * R * 0.92));
}

/** A flat face with normals leaning outwards towards its rim: shades like a gently domed coin, no extra triangles. */
function domed(g: THREE.BufferGeometry): THREE.BufferGeometry {
  const p = g.getAttribute("position"), n = g.getAttribute("normal") as THREE.BufferAttribute;
  const v = new THREE.Vector3();
  for (let i = 0; i < p.count; i++) {
    const side = Math.sign(p.getZ(i)) || 1;
    v.set(p.getX(i) * 0.55, p.getY(i) * 0.55, side).normalize();
    n.setXYZ(i, v.x, v.y, v.z);
  }
  return g;
}

/**
 * `buildSolCoin()` → origin = the coin's centre (the scene sets y = yBase + 0.6), faces ±z, child "spin" turns about
 * y. 588 triangles + a glow sprite; 2 draw calls.
 */
export function buildSolCoin(): THREE.Group {
  return instance("solCoin", () => {
    const metal = { r: 0.24, m: 0.55, e: 0.24 };
    const pearl = { r: 0.38, m: 0, e: 0.3 };
    const kit = new Kit();
    // field (front centre → out), a ridged lip, the band, and back again: solid on the right of the walk
    const field: V2[] = [[0, FIELD], [0.45, FIELD]];
    const edge: V2[] = [[0.45, FIELD], [0.51, RIM], [R, 0.05], [R, -0.05], [0.51, -RIM], [0.45, -FIELD]];
    const back: V2[] = [[0.45, -FIELD], [0, -FIELD]];
    kit.add(domed(revolve(field, SEG)), pearl, 0xf6f2ff);
    kit.add(domed(revolve(back, SEG)), pearl, 0xf6f2ff);
    kit.add(revolve(edge, SEG), metal, gradient);
    const mark = solMark(0.58, 0.03, 0.022);
    kit.add(mark, metal, gradient, { p: [0, 0, FIELD - 0.002] });
    kit.add(mark, metal, gradient, { p: [0, 0, -FIELD + 0.002], r: [0, Math.PI, 0] });
    const spin = kit.build({ name: "spin" });
    const root = new THREE.Group();
    root.name = "solCoin";
    root.userData.anim = "coin";
    root.add(glowSprite(0xb48cff, 2.3, 0.7, 0.62), spin);
    return root;
  });
}
